<script setup lang="ts">
import type { Schemas } from '#shopware';
import { getTranslatedProperty } from '@shopware/helpers'

const { public: { isNewPropertyId: IS_NEW_PROPERTY_ID } } = useRuntimeConfig()

const { t } = useI18n()

interface ListingFilter {
    code: string;
    label: string;
    name: string;
    options: Array<Schemas['PropertyGroupOption'] & { count: number }>;
    entities: Array<Schemas['ProductManufacturer'] & { count: number }>;
}

const props = defineProps<{
  filter: ListingFilter;
  selectedFilters: {
    manufacturer: Set<string>;
    properties: Set<string>;
  };
}>();

const emits = defineEmits<{
  'select-filter-value': [value: { code: string; value: string }];
}>();

// Determine if this is a manufacturer or properties filter
const filterType = computed(() => props.filter.options ? 'properties' : 'manufacturer');

// Get the current selected values for this filter type
const currentlySelected = computed(() => props.selectedFilters[filterType.value]);

// Handle checkbox change
function handleCheckboxChange(optionId: string) {
    emits('select-filter-value', {
        code: props.filter.code,
        value: optionId,
    });
}

// Check if an option is selected
function isOptionSelected(optionId: string) {
    return currentlySelected.value.has(optionId);
}

function getUkLabel(option: Schemas['PropertyGroupOption']): string | null {
    const cf = option.customFields as Record<string, string> | null | undefined;
    const val = cf?.gisy_size_uk_label;
    return val && val !== '-' ? val : null;
}
</script>

<template>
    <div class="flex flex-col">
        <FoundationLabel
            v-for="option in filter.options || filter.entities" 
            :key="option.id"
            class="flex items-start font-normal mb-4 pb-4"
        >
            <FoundationCheckbox
                v-if="getTranslatedProperty(option, 'name') !== '-'"
                :model-value="isOptionSelected(option.id)"
                type="checkbox"
                :value="option.id"
                @update:model-value="() => handleCheckboxChange(option.id)"
            />
            <template v-if="getUkLabel(option) && getTranslatedProperty(option, 'name') !== '-'">
                <div class="flex justify-between w-full">
                    <span>
                        {{ getTranslatedProperty(option, 'name') }}
                    </span>
                    <span>
                        (UK {{ getUkLabel(option) }})
                    </span>
                </div>
            </template>
            <template v-else-if="getTranslatedProperty(option, 'name') !== '-'">
                {{ option.id === IS_NEW_PROPERTY_ID ? t('listing.filter.isNewCheckboxLabel') : getTranslatedProperty(option, 'name') }}
            </template>
        </FoundationLabel>
    </div>
</template>
